import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface SessionRating {
  id: string
  sessionId: string
  userId: string
  flavor: number
  construction: number
  burn: number
  value: number
  overall: number
  isOverallManual: boolean
  flavorNotes?: string | null
  constructionNotes?: string | null
  burnNotes?: string | null
  valueNotes?: string | null
  overallNotes?: string | null
  createdAt: string
  updatedAt: string
}

export interface RatingInput {
  flavor: number
  construction: number
  burn: number
  value: number
  overall?: number
  isOverallManual?: boolean
  flavorNotes?: string
  constructionNotes?: string
  burnNotes?: string
  valueNotes?: string
  overallNotes?: string
}

export interface RatingWeights {
  flavor: number
  construction: number
  burn: number
  value: number
}

export interface CigarRatingHistory {
  ratings: Array<SessionRating & { session: { id: string; smokedDate: string; cigarId: string } }>
  averages: {
    flavor: number
    construction: number
    burn: number
    value: number
    overall: number
    sessionCount: number
  } | null
}

export const ratingApi = createApi({
  reducerPath: 'ratingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/smoking-sessions',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['SessionRating', 'RatingPreferences'],
  endpoints: (builder) => ({
    getSessionRating: builder.query<SessionRating | null, string>({
      query: (sessionId) => `/${sessionId}/rating`,
      transformResponse: (response: { data: { rating: SessionRating | null } }) => response.data.rating,
      providesTags: (_r, _e, sessionId) => [{ type: 'SessionRating', id: sessionId }],
    }),
    upsertSessionRating: builder.mutation<
      { rating: SessionRating; calculatedOverall: number },
      { sessionId: string; rating: RatingInput }
    >({
      query: ({ sessionId, rating }) => ({
        url: `/${sessionId}/rating`,
        method: 'PUT',
        body: rating,
      }),
      transformResponse: (response: { data: { rating: SessionRating; calculatedOverall: number } }) => response.data,
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'SessionRating', id: sessionId },
        'RatingPreferences',
      ],
    }),
    previewOverall: builder.mutation<{ overall: number; weightedOverall: number }, RatingInput>({
      query: (body) => ({
        url: '/ratings/preview',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { overall: number; weightedOverall: number } }) => response.data,
    }),
    getRatingPreferences: builder.query<{ weights: RatingWeights; defaults: RatingWeights }, void>({
      query: () => '/ratings/preferences',
      transformResponse: (response: { data: { weights: RatingWeights; defaults: RatingWeights } }) => response.data,
      providesTags: ['RatingPreferences'],
    }),
    updateRatingPreferences: builder.mutation<{ weights: RatingWeights }, RatingWeights>({
      query: (weights) => ({
        url: '/ratings/preferences',
        method: 'PUT',
        body: weights,
      }),
      transformResponse: (response: { data: { weights: RatingWeights } }) => response.data,
      invalidatesTags: ['RatingPreferences'],
    }),
    getCigarRatingHistory: builder.query<CigarRatingHistory, string>({
      query: (cigarId) => `/cigar/${cigarId}/ratings`,
      transformResponse: (response: { data: CigarRatingHistory }) => response.data,
      providesTags: (_r, _e, cigarId) => [{ type: 'SessionRating', id: `cigar-${cigarId}` }],
    }),
    compareRatings: builder.query<
      { summaries: unknown[]; comparisons: Array<{ cigarA: string; cigarB: string; similarity: number }> },
      string[]
    >({
      query: (cigarIds) => ({
        url: '/ratings/compare',
        params: { cigarIds: cigarIds.join(',') },
      }),
      transformResponse: (response: { data: { summaries: unknown[]; comparisons: Array<{ cigarA: string; cigarB: string; similarity: number }> } }) =>
        response.data,
    }),
  }),
})

export const {
  useGetSessionRatingQuery,
  useUpsertSessionRatingMutation,
  usePreviewOverallMutation,
  useGetRatingPreferencesQuery,
  useUpdateRatingPreferencesMutation,
  useGetCigarRatingHistoryQuery,
  useCompareRatingsQuery,
} = ratingApi
